import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackCompanyCreated, trackTrialStarted } from '@/utils/amplitudeEvents';
import AuthLayout from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, Loader2 } from 'lucide-react';

const COMPANY_SIZES = [
  { value: 'solo', label: 'Just me' },
  { value: 'small', label: '2-10 employees' },
  { value: 'medium', label: '11-50 employees' },
  { value: 'large', label: '51-200 employees' },
  { value: 'enterprise', label: '200+ employees' },
];

const CompanyOnboarding: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [companyName, setCompanyName] = useState('');
  const [companySize, setCompanySize] = useState('small');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!companyName.trim()) {
      toast({
        title: 'Company name required',
        description: 'Please enter a name for your company.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.rpc('create_company_for_user', {
        p_company_name: companyName.trim(),
        p_company_size: companySize,
      });

      if (error) {
        console.error('Error creating company:', error);
        toast({
          title: 'Error creating company',
          description: error.message || 'Something went wrong. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      // Type the response data
      const companyData = data as { id: string; name: string; slug: string; active: boolean };
      // EVENT 2: Company Created
      trackCompanyCreated({
        company_id: companyData.id,
        company_name: companyData.name,
        owner_email: user?.email || undefined,
        company_size: companySize,
      });

      // EVENT 3: Trial Started (companies start on trial by default)
      const { data: companyRecord } = await supabase
        .from('companies')
        .select('trial_ends_at')
        .eq('id', companyData.id)
        .single();
      
      trackTrialStarted({
        company_id: companyData.id,
        company_name: companyData.name,
        owner_email: user?.email || undefined,
        trial_ends_at: companyRecord?.trial_ends_at || undefined,
        started_at: new Date().toISOString(),
      });
      
      // Dispatch event to trigger CompanyContext refresh
      window.dispatchEvent(new CustomEvent('company-created', { 
        detail: { companyId: companyData.id } 
      }));

      toast({
        title: 'Company created!',
        description: `Welcome to ${companyData.name}. Let's get started.`,
      });

      // Short delay to allow context to update
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 500);
      
    } catch (err) {
      console.error('Unexpected error:', err);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'there';

  return (
    <AuthLayout 
      title="Create Your Company" 
      subtitle={`Hey ${userName}! Set up your organization to get started.`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="companyName">Company Name</Label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="companyName"
              type="text"
              placeholder="Acme Inc."
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="pl-10"
              disabled={isSubmitting}
              autoFocus
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="companySize">Company Size</Label>
          <Select 
            value={companySize} 
            onValueChange={setCompanySize}
            disabled={isSubmitting}
          >
            <SelectTrigger id="companySize">
              <SelectValue placeholder="Select company size" />
            </SelectTrigger>
            <SelectContent>
              {COMPANY_SIZES.map((size) => (
                <SelectItem key={size.value} value={size.value}>
                  {size.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isSubmitting || !companyName.trim()}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Company'
          )}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default CompanyOnboarding;
